import React from "react";
import Appointment from "../interface/appointment";
import SearchControllsProps from "../interface/searchControllsProps";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import IPagination from "../interface/iPagination";
import { User, AppContext } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";
import ISortOptions from "../interface/iSortOptions";

export const AppointmentOverviewContext = React.createContext<any>({});

const AppointmentOverviewProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [appointments, setAppointments] = React.useState<Appointment[] | null>(
    null
  );

  // Pagination settings
  const [paginationSettings, setPaginationSettings] =
    React.useState<IPagination>({
      page: 1,
      rowsPerPage: 15,
    });

  // Search controls state
  const [searchControlls, setSearchControlls] =
    React.useState<SearchControllsProps>({
      date: {
        start: new Date().toISOString().split("T")[0],
        end: new Date().toISOString().split("T")[0],
      },
      coach: {
        label: "Alle",
        value: null,
      },
      status: {
        free: true,
        missed: true,
        cancelled: true,
        active: true,
        completed: true,
        speedcoaching: true,
      },
      loading: false,
    });

  // State to hold the appointment to be deleted
  const [deleteAppointment, setDeleteAppointment] =
    React.useState<Appointment | null>(null);

  // Function to fetch appointments based on search controls
  const fetchAppointments = React.useCallback((): void => {
    setSearchControlls({ ...searchControlls, loading: true });
    setAppointments(null);

    // reset sorting options
    setSortOptions({
      date: "none",
      type: "none",
      participant: "none",
      coach: "none",
      status: "none",
    });

    const url =
      user.role?.label === "Admin"
        ? endpoints.admin.appointmentOverview
        : endpoints.coach.appointmentOverview;

    axios
      .post(url, {
        sessionId: user.sessionId,
        fromDate: searchControlls.date.start,
        tillDate: searchControlls.date.end,
        coachId:
          user.role?.label === "Admin" ? searchControlls.coach?.value : null,
        // endpoint expects status as an object with boolean values
        status: {
          booked: true,
          free: true,
          missed: true,
        },
      })
      .then((response) => {
        checkSession(response);

        // add expand property to each appointment
        response.data.data.map(
          (appointment: Appointment) => (appointment.expand = false)
        );

        setAppointments(response.data.data);
        setSearchControlls({ ...searchControlls, loading: false });
      })
      .catch((error) => {
        console.error("Error fetching appointment overview", error);
        setSearchControlls({ ...searchControlls, loading: false });
      });
  }, [checkSession, searchControlls, user]);

  // Function to delete an appointment
  const doDeleteAppointment = React.useCallback((): void => {
    axios
      .post(endpoints.admin.deleteAppointment, {
        sessionId: user.sessionId,
        appointmentId: deleteAppointment?.id,
      })
      .then((response) => {
        checkSession(response);

        if (response.data.meta.status === "success") {
          enqueueSnackbar("Termin gelöscht!", {
            variant: "success",
          });
          setDeleteAppointment(null);
          setAppointments(null);
          fetchAppointments();
        } else {
          enqueueSnackbar(response.data.meta.message, {
            variant: "error",
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar("Termin konnte nicht gelöscht werden!", {
          variant: "error",
        });
        console.error(error);
      });
  }, [checkSession, deleteAppointment?.id, fetchAppointments, user.sessionId]);

  // Function to determine the background color based on appointment status
  const getBackgroundColor = (appointment: Appointment): string => {
    // appointment status is "G" for booked
    if (appointment.status === "G") {
      // appointment is today or in the future
      if (
        new Date(appointment.date).toISOString().split("T")[0] >=
        new Date().toISOString().split("T")[0]
      )
        return "success.light";

      // appointment is in the past
      return "secondary.light";
    }

    // appointment status is "V" for missed
    if (appointment.status === "V") return "error.light";

    // appointment status is "K" for cancelled
    if (appointment.status === "K") return "warning.light";

    // appointment status is "F" for free
    if (appointment.status === "F") {
      // appointment is a speed coaching
      if (appointment.isSpeedcoaching) return "info.main";

      // appointment is not a speed coaching
      return "info.light";
    }

    // fallback case
    return "#eee"; // default white
  };

  // Function to filter appointments based on search controls
  const getFiltertedAppointments = React.useCallback((): Appointment[] => {
    return (
      appointments?.filter((appointment: Appointment) => {
        return (
          // appointment status is "F" and is not a speed coaching
          (searchControlls.status.free &&
            appointment.status === "F" &&
            !appointment.isSpeedcoaching) ||
          // appointment status is "F" and is a speed coaching
          (searchControlls.status.speedcoaching &&
            appointment.status === "F" &&
            appointment.isSpeedcoaching) ||
          // appointment status is "V" for missed
          (searchControlls.status.missed && appointment.status === "V") ||
          // appointment status is "K" for cancelled
          (searchControlls.status.cancelled && appointment.status === "K") ||
          // appointment status is "G" for booked and today or in the future
          (searchControlls.status.active &&
            appointment.status === "G" &&
            new Date(appointment.date).toISOString().split("T")[0] >=
              new Date().toISOString().split("T")[0]) ||
          // appointment status is "G" for booked and in the past
          (searchControlls.status.completed &&
            appointment.status === "G" &&
            new Date(appointment.date).toISOString().split("T")[0] <
              new Date().toISOString().split("T")[0])
        );
      }) || []
    );
  }, [
    appointments,
    searchControlls.status.active,
    searchControlls.status.cancelled,
    searchControlls.status.completed,
    searchControlls.status.free,
    searchControlls.status.missed,
    searchControlls.status.speedcoaching,
  ]);

  // Fetch appointments on initial render
  React.useEffect(() => {
    if (searchControlls.loading) return;
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State to hold the sorting options
  const [sortOptions, setSortOptions] = React.useState<ISortOptions>({
    date: "none",
    type: "none",
    participant: "none",
    coach: "none",
    status: "none",
  });

  // Function to set the sorting option for participants
  const setSortOptionParticipant = React.useCallback((): void => {
    // Toggle the sorting order for participants
    switch (sortOptions.participant) {
      // If currently sorted in ascending order, switch to descending
      case "asc":
        sortOptions.participant = "desc";
        break;

      // If currently sorted in descending order, switch to none (no sorting)
      case "desc":
        sortOptions.participant = "none";
        break;

      // If currently not sorted, switch to ascending order
      default:
        sortOptions.participant = "asc";
        break;
    }

    // Update the sort options state
    setSortOptions(sortOptions);
  }, [sortOptions]);

  // State to hold the filtered appointments based on search controls
  const [filtertedAppointments, setFiltertedAppointments] = React.useState<
    Appointment[] | null
  >(getFiltertedAppointments() || null);

  // Effect to update filtered appointments whenever search controls or appointments change
  const sortAppopintmentsByParticipants = React.useCallback((): void => {
    // Sort the appointments based on the selected option
    if (filtertedAppointments) {
      if (sortOptions.participant === "none") {
        // If no sorting is applied, return the original order
        setFiltertedAppointments(getFiltertedAppointments() || []);
        return;
      }

      // Create a copy of the filtered appointments and sort them
      const sortedAppointments: Appointment[] = [...filtertedAppointments].sort(
        (a: Appointment, b: Appointment) => {
          // create temporary names without "Frau" or "Herr"
          // to ensure consistent sorting regardless of prefix
          const aTempName: string | null = a.participantFullName
            ? a.participantFullName.replace(/^(Frau|Herr)\s+/i, "")
            : null;

          const bTempName: string | null = b.participantFullName
            ? b.participantFullName.replace(/^(Frau|Herr)\s+/i, "")
            : null;

          // Compare the temporary names for sorting
          if (sortOptions.participant === "asc") {
            // Sort by¸ participantFullName in ascending order
            return aTempName?.localeCompare(bTempName || "") || 0;
          }

          // If sorting is in descending order
          // compare the temporary names in reverse order
          if (sortOptions.participant === "desc") {
            // Sort by participantFullName in descending order
            return bTempName?.localeCompare(aTempName || "") || 0;
          }

          // If no sorting is applied, return 0 to keep the original order
          return 0;
        }
      );

      // Update the filtered appointments with the sorted appointments
      setFiltertedAppointments(sortedAppointments);
    }
  }, [
    filtertedAppointments,
    getFiltertedAppointments,
    sortOptions.participant,
  ]);

  // Effect to update filtered appointments whenever search controls or appointments change
  const sortParticipants = React.useCallback((): void => {
    setSortOptionParticipant();
    sortAppopintmentsByParticipants();
  }, [setSortOptionParticipant, sortAppopintmentsByParticipants]);

  // Effect to fetch appointments when the component mounts or search controls change
  React.useEffect(() => {
    const appointments = getFiltertedAppointments();
    if (appointments) {
      setFiltertedAppointments(appointments);
    }
  }, [getFiltertedAppointments, setFiltertedAppointments]);

  const value: {} = React.useMemo(
    () => ({
      appointments,
      setAppointments,
      searchControlls,
      setSearchControlls,
      fetchAppointments,
      deleteAppointment,
      setDeleteAppointment,
      doDeleteAppointment,
      getBackgroundColor,
      paginationSettings,
      setPaginationSettings,
      getFiltertedAppointments,
      sortParticipants,
      setFiltertedAppointments,
      filtertedAppointments,
      sortOptions,
      setSortOptions,
    }),
    [
      appointments,
      searchControlls,
      fetchAppointments,
      deleteAppointment,
      doDeleteAppointment,
      paginationSettings,
      getFiltertedAppointments,
      sortParticipants,
      filtertedAppointments,
      sortOptions,
    ]
  );

  return (
    <AppointmentOverviewContext.Provider value={value}>
      {children}
    </AppointmentOverviewContext.Provider>
  );
};

export default AppointmentOverviewProvider;
